// src/components/tabs/AppointmentsStats.tsx
import React from "react";
import "../../style/css/Stats.css";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from "recharts";
import Error403Page from "../Error_backend";

interface AppointmentsData {
  statusCounts: {
    total: number;
    confirmed: number;
    pending: number;
    cancelled: number;
    completed: number;
    noshow: number;
  };
  appointmentsByHour: Array<{ hour: string; count: number }>;
  appointmentsByDay: Array<{ day: string; count: number }>;
  summary: {
    cancellationRate: number;
    completionRate: number;
    noShowRate: number;
    pendingRate: number;
  };
}

interface AppointmentsStatsProps {
  data: AppointmentsData | null;
  loading: boolean;
  error: string | null;
  dateRange: { startDate: string; endDate: string };
  onRetry?: () => void;
}

// Color schemes for charts
const STATUS_COLORS = {
  confirmed: "#8884d8",
  pending: "#ffc658",
  cancelled: "#ff8042",
  completed: "#00C49F",
  noshow: "#FF8042",
  total: "#0088FE",
};

const RATE_COLORS = ["#8884d8", "#00C49F", "#FF8042", "#FFBB28"];

const AppointmentsStats: React.FC<AppointmentsStatsProps> = ({
  data,
  loading,
  error,
  dateRange,
  onRetry,
}) => {
  // Show error page for 403
  if (error) {
    return <Error403Page />;
  }

  // Show loading state
  if (loading) {
    return (
      <div className="stats-dashboard-header">
        <div className="loading-state">Cargando estadísticas de citas...</div>
      </div>
    );
  }

  // Show empty state
  if (!data) {
    return (
      <div className="stats-dashboard-header">
        <div className="empty-state">No hay datos disponibles</div>
      </div>
    );
  }

  // Prepare data for charts - use simpler format for Recharts
  const statusData = [
    {
      name: "Confirmadas",
      value: data.statusCounts.confirmed,
      fill: STATUS_COLORS.confirmed,
    },
    {
      name: "Pendientes",
      value: data.statusCounts.pending,
      fill: STATUS_COLORS.pending,
    },
    {
      name: "Canceladas",
      value: data.statusCounts.cancelled,
      fill: STATUS_COLORS.cancelled,
    },
    {
      name: "Completadas",
      value: data.statusCounts.completed,
      fill: STATUS_COLORS.completed,
    },
    {
      name: "No Show",
      value: data.statusCounts.noshow,
      fill: STATUS_COLORS.noshow,
    },
  ];

  const ratesData = [
    {
      name: "Tasa de Cancelación",
      value: data.summary.cancellationRate,
      fill: RATE_COLORS[0],
    },
    {
      name: "Tasa de Completación",
      value: data.summary.completionRate,
      fill: RATE_COLORS[1],
    },
    {
      name: "Tasa de No Show",
      value: data.summary.noShowRate,
      fill: RATE_COLORS[2],
    },
    {
      name: "Tasa de Pendientes",
      value: data.summary.pendingRate,
      fill: RATE_COLORS[3],
    },
  ];

  // Format hour data for better display
  const hourData = data.appointmentsByHour.map((item) => ({
    hour: item.hour,
    Citas: item.count,
  }));

  // Format day data (translate to Spanish if needed)
  const dayTranslations: { [key: string]: string } = {
    Monday: "Lunes",
    Tuesday: "Martes",
    Wednesday: "Miércoles",
    Thursday: "Jueves",
    Friday: "Viernes",
    Saturday: "Sábado",
    Sunday: "Domingo",
  };

  const dayData = data.appointmentsByDay.map((item) => ({
    day: dayTranslations[item.day] || item.day,
    Citas: item.count,
  }));

  // Custom tooltip formatter
  const formatTooltip = (value: number, name: string) => {
    if (name.includes("Tasa")) {
      return [`${value.toFixed(1)}%`, name];
    }
    return [value, name];
  };

  // Calculate total for percentage display
  const totalAppointments = data.statusCounts.total;

  return (
    <div className="stats-dashboard-header">
      {/* Key Metrics Summary */}
      <div className="metrics-summary">
        <div className="metric-card">
          <h3>Total Citas</h3>
          <div className="metric-value">{data.statusCounts.total}</div>
        </div>
        <div className="metric-card">
          <h3>Tasa de Completación</h3>
          <div className="metric-value">
            {data.summary.completionRate.toFixed(1)}%
          </div>
        </div>
        <div className="metric-card">
          <h3>Tasa de Cancelación</h3>
          <div className="metric-value">
            {data.summary.cancellationRate.toFixed(1)}%
          </div>
        </div>
        <div className="metric-card">
          <h3>Tasa de No Show</h3>
          <div className="metric-value">
            {data.summary.noShowRate.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* 1. Status Counts and Ratios */}
      <div className="stats-section">
        <h3>Distribución de Citas por Estado</h3>
        <div className="charts-grid">
          {/* Status Counts Pie Chart */}
          <div className="chart-container">
            <h4>Distribución por Estado</h4>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  // label={({ name, value }) => {
                  //   const percentage = (
                  //     (value / totalAppointments) *
                  //     100
                  //   ).toFixed(1);
                  //   return `${name}: ${value} (${percentage}%)`;
                  // }}
                  outerRadius={80}
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(
                    value: number | undefined,
                    name: string | undefined
                  ) => {
                    if (value === undefined) return ["0 (0%)", name || ""];
                    const percentage = (
                      (value / totalAppointments) *
                      100
                    ).toFixed(1);
                    return [`${value} (${percentage}%)`, name || ""];
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Rates Bar Chart */}
          <div className="chart-container">
            <h4>Porcentajes y Tasas</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={ratesData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  domain={[0, 100]}
                  tickFormatter={(value: number) => `${value}%`}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={150}
                  tick={{ fontSize: 12 }}
                />
                <Tooltip
                  formatter={(
                    value: number | undefined,
                    name: string | undefined
                  ) => [`${value ? value.toFixed(1) : "0"}%`, "Porcentaje"]}
                />
                <Bar dataKey="value" name="Porcentaje">
                  {ratesData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* 2. Appointments by Hour */}
      <div className="stats-section">
        <h3>Citas por Hora del Día</h3>
        <div className="chart-container">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={hourData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="hour"
                angle={-45}
                textAnchor="end"
                height={80}
                interval={0}
              />
              <YAxis />
              <Tooltip
                formatter={(
                  value: number | undefined,
                  name: string | undefined
                ) => [value ?? 0, "Citas"]}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="Citas"
                stroke="#8884d8"
                strokeWidth={2}
                dot={{ fill: "#8884d8", strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: "#8884d8", strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 3. Appointments by Day */}
      <div className="stats-section">
        <h3>Citas por Día de la Semana</h3>
        <div className="chart-container">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dayData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip
                formatter={(
                  value: number | undefined,
                  name: string | undefined
                ) => [value ?? 0, "Citas"]}
              />
              <Legend />
              <Bar dataKey="Citas" fill="#00C49F" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AppointmentsStats;
